import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Check, Sparkles } from 'lucide-react';
import { WORKERS } from '../mock';
import { hireWorker } from '../api';
import { toast } from '../hooks/use-toast';

const gradientForIndex = (i) => {
  const gradients = [
    'from-rose-100 to-orange-100',
    'from-emerald-100 to-teal-100',
    'from-sky-100 to-blue-100',
    'from-violet-100 to-fuchsia-100',
    'from-amber-100 to-yellow-100',
  ];
  return gradients[i % gradients.length];
};

const MatchResultsDialog = ({ open, onOpenChange, loading, result, prompt }) => {
  const [hiring, setHiring] = React.useState(null);
  const navigate = useNavigate();

  const rosterMap = React.useMemo(() => Object.fromEntries(WORKERS.map((w) => [w.id, w])), []);

  const doHire = async ({ workerId, workerName, role, isCustom }) => {
    setHiring(workerId);
    try {
      await hireWorker({ workerId, workerName, role, isCustom });
      toast({ title: `Hired ${workerName}`, description: 'Opening your workspace…' });
      onOpenChange(false);
      setTimeout(() => navigate('/workspace'), 400);
    } catch (e) {
      toast({ title: 'Hire failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setHiring(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl tracking-tight">Your top matches</DialogTitle>
          <DialogDescription className="line-clamp-2">
            {prompt ? `Based on: “${prompt}”` : 'Ready-to-hire workers curated for your task.'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-14 text-neutral-500">
            <Loader2 className="mb-3 h-6 w-6 animate-spin" />
            <p className="text-sm">Matching workers to your task…</p>
          </div>
        )}

        {!loading && result && (
          <div className="space-y-6">
            {/* Recommendations */}
            <div>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">Recommended from roster</h3>
              <div className="space-y-2">
                {result.recommendations.map((rec) => {
                  const w = rosterMap[rec.id];
                  if (!w) return null;
                  return (
                    <div key={rec.id} className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-white p-3 transition-colors hover:border-neutral-300">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${w.avatarColor} font-semibold text-neutral-800`}>
                        {w.initial}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <p className="truncate font-semibold text-neutral-900">{w.name}</p>
                          <p className="truncate text-xs text-neutral-500">{w.role}</p>
                        </div>
                        <p className="mt-0.5 text-sm text-neutral-600">{rec.reason}</p>
                      </div>
                      <Button
                        size="sm"
                        disabled={hiring === w.id}
                        onClick={() => doHire({ workerId: w.id, workerName: w.name, role: w.role, isCustom: false })}
                        className="shrink-0 bg-neutral-900 text-white hover:bg-neutral-800"
                      >
                        {hiring === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Hire'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Custom worker */}
            {result.custom_worker && (
              <div>
                <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                  <Sparkles className="h-3.5 w-3.5" />
                  Custom-built for your task
                </h3>
                <div className="rounded-xl border border-neutral-900/10 bg-gradient-to-br from-neutral-50 to-white p-4 ring-1 ring-inset ring-neutral-900/5">
                  <div className="mb-3 flex items-start gap-3">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br ${gradientForIndex(0)} font-semibold text-neutral-800`}>
                      {result.custom_worker.name?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold text-neutral-900">{result.custom_worker.name}</p>
                      <p className="truncate text-sm text-neutral-500">{result.custom_worker.role}</p>
                    </div>
                  </div>
                  <p className="mb-3 text-sm leading-relaxed text-neutral-700">{result.custom_worker.description}</p>
                  {result.custom_worker.capabilities?.length > 0 && (
                    <ul className="mb-3 space-y-1.5">
                      {result.custom_worker.capabilities.map((c, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-neutral-700">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={2.5} />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {result.custom_worker.tags?.length > 0 && (
                    <div className="mb-4 flex flex-wrap gap-1.5">
                      {result.custom_worker.tags.map((t) => (
                        <span key={t} className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-600">{t}</span>
                      ))}
                    </div>
                  )}
                  <Button
                    disabled={hiring === 'custom'}
                    onClick={() => doHire({
                      workerId: `custom-${result.id}`,
                      workerName: result.custom_worker.name,
                      role: result.custom_worker.role,
                      isCustom: true,
                    })}
                    className="w-full bg-neutral-900 text-white hover:bg-neutral-800"
                  >
                    {hiring === 'custom' ? <Loader2 className="h-4 w-4 animate-spin" /> : `Hire ${result.custom_worker.name}`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MatchResultsDialog;
